import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Lock, LogOut, Search, Trash2, CheckCircle2, Circle, Mail, RefreshCw,
  Inbox, Loader2, AlertCircle, Bell,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "mc_admin_token";

const tipoColors = {
  Hotel: "bg-blue-100 text-blue-700",
  Bar: "bg-amber-100 text-amber-700",
  Ristorante: "bg-rose-100 text-rose-700",
  "Privato / B2C": "bg-emerald-100 text-emerald-700",
  "Partner ALLF": "bg-violet-100 text-violet-700",
};

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onLogin(res.data.token);
    } catch (e) {
      setErr("Password errata. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#F3EFE7] px-5">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-black/5 p-8">
        <div className="flex items-center gap-2 mb-6">
          <img src="/logo-mc.png" alt="MichelChef" className="h-9 w-auto" />
          <span className="font-display font-semibold text-[#3B342E]/70 text-sm">· Admin</span>
        </div>
        <h1 className="font-display font-bold text-2xl text-[#14110F]">Area riservata</h1>
        <p className="text-[#3B342E]/70 mt-2 text-sm">Inserisci la password per gestire i messaggi.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="relative">
            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#3B342E]/40" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password admin"
              autoFocus
              className="w-full rounded-xl border border-black/10 bg-white pl-11 pr-4 py-3 outline-none focus:border-[#17784A] focus:ring-2 focus:ring-[#17784A]/20 transition"
            />
          </div>
          {err && <div className="flex items-center gap-2 text-[#D8382E] text-sm"><AlertCircle size={16} /> {err}</div>}
          <button type="submit" disabled={loading}
            className="btn-press w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#17784A] text-white py-3 font-semibold hover:bg-[#13633c] disabled:opacity-70">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={18} />} Accedi
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [filterTipo, setFilterTipo] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [lastSeen, setLastSeen] = useState(() => Number(localStorage.getItem("mc_admin_lastseen") || 0));

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const logout = () => {
    localStorage.setItem("mc_admin_lastseen", String(Date.now()));
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const markSeen = () => {
    const now = Date.now();
    localStorage.setItem("mc_admin_lastseen", String(now));
    setLastSeen(now);
  };

  const toggleStatus = async (m) => {
    const next = m.status === "gestito" ? "da_gestire" : "gestito";
    setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, status: next } : x)));
    try {
      await axios.patch(`${API}/admin/messages/${m.id}`, { status: next }, auth);
    } catch (e) { load(); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Eliminare il messaggio di ${m.nome}?`)) return;
    setMessages((prev) => prev.filter((x) => x.id !== m.id));
    try {
      await axios.delete(`${API}/admin/messages/${m.id}`, auth);
    } catch (e) { load(); }
  };

  if (!token) return <Login onLogin={setToken} />;

  const tipos = ["all", ...Array.from(new Set(messages.map((m) => m.tipo)))];
  const filtered = messages.filter((m) => {
    const q = query.trim().toLowerCase();
    const okQ = !q || m.nome.toLowerCase().includes(q) || m.email.toLowerCase().includes(q) || m.messaggio.toLowerCase().includes(q);
    const okT = filterTipo === "all" || m.tipo === filterTipo;
    const okS = filterStatus === "all" || m.status === filterStatus;
    return okQ && okT && okS;
  });
  const daGestire = messages.filter((m) => m.status !== "gestito").length;
  const unread = messages.filter((m) => new Date(m.created_at).getTime() > lastSeen).length;

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#F3EFE7]">
      {/* Top bar */}
      <header className="bg-[#14110F] text-white sticky top-0 z-10">
        <div className="tricolor-bar h-1 w-full" />
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center bg-white rounded-lg px-2 py-1">
              <img src="/logo-mc.png" alt="MichelChef" className="h-6 w-auto" />
            </span>
            <span className="font-display font-bold">· Messaggi</span>
            {unread > 0 && (
              <span className="ml-1 inline-flex items-center gap-1.5 rounded-full bg-[#D8382E] text-white text-xs font-bold px-2.5 py-1">
                <Bell size={13} /> {unread} nuovi
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unread > 0 && (
              <button onClick={markSeen} className="btn-press hidden sm:inline-flex items-center gap-2 text-sm text-white/80 hover:text-white border border-white/20 rounded-full px-4 py-2">
                Segna come letti
              </button>
            )}
            <button onClick={logout} className="btn-press inline-flex items-center gap-2 text-sm text-white/80 hover:text-white border border-white/20 rounded-full px-4 py-2">
              <LogOut size={16} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { icon: Inbox, label: "Totale messaggi", value: messages.length, c: "text-[#17784A]" },
            { icon: Circle, label: "Da gestire", value: daGestire, c: "text-[#D8382E]" },
            { icon: CheckCircle2, label: "Gestiti", value: messages.length - daGestire, c: "text-[#17784A]" },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 flex items-center gap-4">
              <span className={`grid place-items-center w-11 h-11 rounded-xl bg-[#F3EFE7] ${s.c}`}><s.icon size={22} /></span>
              <div>
                <div className="font-display font-extrabold text-2xl text-[#14110F]">{s.value}</div>
                <div className="text-xs text-[#3B342E]/60">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-4 mb-6 flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#3B342E]/40" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca nome, email, testo..."
              className="w-full rounded-xl border border-black/10 pl-11 pr-4 py-2.5 outline-none focus:border-[#17784A] focus:ring-2 focus:ring-[#17784A]/20 transition" />
          </div>
          <select value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)}
            className="rounded-xl border border-black/10 px-4 py-2.5 outline-none focus:border-[#17784A]">
            {tipos.map((t) => <option key={t} value={t}>{t === "all" ? "Tutti i tipi" : t}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-xl border border-black/10 px-4 py-2.5 outline-none focus:border-[#17784A]">
            <option value="all">Tutti gli stati</option>
            <option value="da_gestire">Da gestire</option>
            <option value="gestito">Gestiti</option>
          </select>
          <button onClick={load} className="btn-press inline-flex items-center gap-2 rounded-xl bg-[#17784A]/10 text-[#17784A] px-4 py-2.5 font-medium hover:bg-[#17784A]/20">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Aggiorna
          </button>
        </div>

        {/* List */}
        {loading && messages.length === 0 ? (
          <div className="text-center py-20 text-[#3B342E]/50"><Loader2 size={30} className="animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[#3B342E]/50">
            <Inbox size={40} className="mx-auto text-[#17784A]/30" />
            <p className="mt-4">Nessun messaggio trovato.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((m) => (
              <div key={m.id} className={`bg-white rounded-2xl border shadow-sm p-5 transition ${m.status === "gestito" ? "border-[#17784A]/30 opacity-80" : "border-black/5"}`}>
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display font-bold text-[#14110F]">{m.nome}</h3>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${tipoColors[m.tipo] || "bg-gray-100 text-gray-700"}`}>{m.tipo}</span>
                      {m.status === "gestito" && <span className="inline-flex items-center gap-1 text-[#17784A] text-xs font-semibold"><CheckCircle2 size={13} /> Gestito</span>}
                    </div>
                    <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 text-sm text-[#17784A] mt-1 hover:underline"><Mail size={13} /> {m.email}</a>
                    <p className="mt-3 text-[#3B342E]/85 leading-relaxed">{m.messaggio}</p>
                    <p className="mt-2 text-xs text-[#3B342E]/45">{fmt(m.created_at)}</p>
                  </div>
                  <div className="flex sm:flex-col gap-2 shrink-0">
                    <button onClick={() => toggleStatus(m)}
                      className={`btn-press inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium ${m.status === "gestito" ? "bg-[#F3EFE7] text-[#3B342E]" : "bg-[#17784A] text-white hover:bg-[#13633c]"}`}>
                      {m.status === "gestito" ? <><Circle size={15} /> Riapri</> : <><CheckCircle2 size={15} /> Gestito</>}
                    </button>
                    <button onClick={() => remove(m)}
                      className="btn-press inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium bg-[#D8382E]/10 text-[#D8382E] hover:bg-[#D8382E]/20">
                      <Trash2 size={15} /> Elimina
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
